import {FC, useEffect, useMemo, useRef} from 'react';

import {t} from 'i18next';

import {CarouselListContainer} from 'components/recommendations/carousel-list-container';
import {CarouselProductCardList} from 'components/recommendations/carousel-product-card-list';
import {config} from 'config/env';
import {isBasketDiversifyFlagEnabled} from 'constants/recommendation';
import {RecommendationVariants, ScrollDirection} from 'contexts/analytics-context';
import {useAppSelector} from 'hooks';
import {useAppMode} from 'hooks/useAppMode';
import {useElementVisibleInViewport} from 'hooks/useElementVisibleInViewport';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {productAnalytics} from 'utils';
import {
  getBasketRecommendationAnalyticTitle,
  getBasketRecommendationTitle
} from 'utils/recommendation-utils/recommendations';

const CAROUSEL_IMPRESSION_DEFAULT_DELAY = 3000;

export const BasketRecommendations: FC = () => {
  const {recommendations} = useAppSelector((state) => state.recommendations);
  const {isExpress} = useAppMode();
  const isReactNativeView = ReactNative.isReactNativeWebView();
  const isCarouselImpressionSent = useRef<boolean>(false);
  const recommendedProductsTotal = recommendations?.totalCount;

  const carouselMaxItemsCount = config.SHOPPING_CAROUSEL_MAX_ITEM_COUNT_VIEW;

  const hideSeeAllCard = useMemo(() => {
    const parsed = carouselMaxItemsCount ? JSON.parse(carouselMaxItemsCount) : {};
    const maxCount = Number(parsed?.web || 10);

    return recommendedProductsTotal <= maxCount;
  }, [recommendedProductsTotal, carouselMaxItemsCount]);

  const {isIntersecting: isCarouselDivVisible, setNodeRef: setNodeRefFullVisible} = useElementVisibleInViewport({
    threshold: 1
  });
  const {isIntersecting: isCarouselPartiallyVisible, setNodeRef: setNodeRefPartialVisible} =
    useElementVisibleInViewport({threshold: 0.01, duration: CAROUSEL_IMPRESSION_DEFAULT_DELAY});

  const productsAnalytics = useMemo(
    () =>
      recommendations.products?.map((product) => {
        return {
          ...productAnalytics(product),
          ...(product?.recommendationScore !== undefined && {
            recommendationScore: product?.recommendationScore
          }),
          ...(product?.normalisedRecommendationScore !== undefined && {
            normalisedRecommendationScore: product?.normalisedRecommendationScore
          }),
          ...(product?.alreadyPurchased !== undefined && {
            hasAlreadyPurchased: product?.alreadyPurchased
          })
        };
      }),
    [recommendations.products]
  );

  useEffect(() => {
    if (!isCarouselImpressionSent.current && (isCarouselDivVisible || isCarouselPartiallyVisible)) {
      addAnalyticsRecommendationCarouselImpression();
    }
    if (!isCarouselPartiallyVisible) {
      isCarouselImpressionSent.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCarouselDivVisible, isCarouselPartiallyVisible]);

  const handleOnSeeAllClick = (triggerType: 'seeAll' | 'seeAllCard') => {
    !isReactNativeView && SendBrowserEvent.closeBasketDrawer();
    const analyticProps = {
      carouselName: `${t(getBasketRecommendationAnalyticTitle(recommendations.recommendationType ?? ''))}`,
      triggerType,
      carouselIndex: 0,
      getPersonalizedRecommendationsFromDb: recommendations.recommendationType,
      algorithm: recommendations.algorithm,
      isDiversifyResult: isBasketDiversifyFlagEnabled,
      ...(isReactNativeView && {
        fromExpress: isExpress
      })
    };
    impressionCallOneTime();
    SendBrowserEvent.goToPLP('recommendations', analyticProps);
  };

  const impressionCallOneTime = () => {
    if (!isCarouselImpressionSent.current) {
      addAnalyticsRecommendationCarouselImpression();
    }
  };

  const addAnalyticsRecommendationCarouselImpression = () => {
    if (isCarouselImpressionSent) {
      isCarouselImpressionSent.current = true;
    }
    const analyticsParams = {
      carouselName: `${t(getBasketRecommendationAnalyticTitle(recommendations.recommendationType ?? ''))}`,
      algorithm: recommendations.algorithm,
      getPersonalizedRecommendationsFromDb: recommendations.recommendationType,
      isDiversifyResult: isBasketDiversifyFlagEnabled,
      isCarouselPersonalised: recommendations?.personalized,
      products: productsAnalytics,
      ...(isReactNativeView && {
        fromExpress: isExpress
      })
    };
    SendBrowserEvent.carouselImpressionEvent(analyticsParams);
  };

  const handleOnCarouselArrowClick = (scrollDirection: string) => {
    impressionCallOneTime();
    triggerCarouselScrollAnalytics(scrollDirection);
  };

  const triggerCarouselScrollAnalytics = (scrollDirection?: string) => {
    const analyticsParams = {
      carouselName: `${t(getBasketRecommendationAnalyticTitle(recommendations.recommendationType ?? ''))}`,
      pageName: RecommendationVariants.BASKET,
      algorithm: recommendations.algorithm,
      getPersonalizedRecommendationsFromDb: recommendations.recommendationType,
      isDiversifyResult: isBasketDiversifyFlagEnabled,
      ...(isReactNativeView && {
        fromExpress: isExpress
      }),
      ...(scrollDirection && {scrollDirection})
    };
    SendBrowserEvent.carouselScrollEvent(analyticsParams);
  };

  return (
    <CarouselListContainer
      title={`${t(getBasketRecommendationTitle(recommendations.recommendationType ?? ''))}`}
      carouselAnalytics={{}}
      analyticsEventNameMapper={'recommendation_v2'}
      isFullWidth={true}
      isShowSeeAll={!hideSeeAllCard}
      ref={(node) => {
        setNodeRefPartialVisible(node);
        setNodeRefFullVisible(node);
      }}
      handleOnCarouselLeftClick={() => handleOnCarouselArrowClick(ScrollDirection.LEFT)}
      handleOnCarouselRightClick={() => handleOnCarouselArrowClick(ScrollDirection.RIGHT)}
      handleOnSeeAllClick={handleOnSeeAllClick}
      triggerCarouselScrollAnalytics={triggerCarouselScrollAnalytics}
      isSearchBoxCarousel={true}
      showShadowArrow={true}
      seeAllRouterPath={'recommendations'}
      carouselIndex={0}
    >
      <CarouselProductCardList
        productsData={recommendations?.products}
        carouselAnalytics={{carouselIndex: 1}}
        sendCarouselImpressionAnalytics={impressionCallOneTime}
        isSearchBoxCarousel={false}
      />
    </CarouselListContainer>
  );
};
